import Blockly from 'blockly/browser'
import _ from 'lodash'
import { defineBlock } from './Util'

// Options mutator

defineBlock('sirenia_options_mutator_container', {
  message0: 'Options %1 %2',
  args0: [
    {
      type: 'input_dummy'
    },
    {
      type: 'input_statement',
      name: 'CHOSEN_PROPS'
    }
  ],
  tooltip: 'Which options should be used',
  helpUrl: ''
})

// Remember which option mutator blocks have been created, so we don't create duplicates
const knownOptionMutators = {}

function registerOptionsMutator(mutatorName, options) {
  if (Blockly.Extensions.ALL_[mutatorName]) {
    return // Already registered
  }
  // Create blocks for the individual options for the mutator UI dialog
  for (const optionKey in options) {
    const isKnownMutatorBlock = knownOptionMutators[optionKey]
    const optionObj = (knownOptionMutators[optionKey] =
      knownOptionMutators[optionKey] || options[optionKey])
    if (isKnownMutatorBlock && options[optionKey]) {
      console.warn(
        `Ignoring duplicate mutator option with key ${optionKey} in mutator ${mutatorName}`
      )
    }
    if (!isKnownMutatorBlock) {
      defineBlock('sirenia_options_mutator_' + optionKey, {
        message0: optionObj.label || optionKey, // Fall back to using the option key as label
        previousStatement: null,
        nextStatement: null
      })
    } else {
      options[optionKey] = knownOptionMutators[optionKey]
    }
  }

  Blockly.Extensions.registerMutator(
    mutatorName,
    {
      mutationToDom() {
        // Serialize mutator info to xml
        if (!this.chosenProps || this.chosenProps.length === 0) {
          return null
        }
        let container = document.createElement('mutation')
        container.setAttribute('chosenprops', _.uniq(this.chosenProps).join(','))
        return container
      },
      domToMutation(container) {
        // Restore mutator info from xml
        let encodedProps = container.getAttribute('chosenprops')
        if (encodedProps) {
          this.chosenProps = _.uniq(encodedProps.split(','))
        }
        this.updateShape_()
      },
      decompose(workspace) {
        // Populate the mutator's dialog with the block's components
        let containerBlock = workspace.newBlock('sirenia_options_mutator_container')
        containerBlock.initSvg()
        let connection = containerBlock.getInput('CHOSEN_PROPS').connection
        for (const property of this.chosenProps) {
          let propBlock = workspace.newBlock('sirenia_options_mutator_' + property)
          propBlock.initSvg()
          connection.connect(propBlock.previousConnection)
          connection = propBlock.nextConnection
        }
        return containerBlock
      },
      compose(containerBlock) {
        // Reconfigure this block based on the mutator dialog's components
        let propBlock = containerBlock.getInputTargetBlock('CHOSEN_PROPS')
        let newPropSet = []
        // Get new prop selection
        while (propBlock) {
          newPropSet.push(/([^_]*$)/.exec(propBlock.type)[1])
          propBlock = propBlock.nextConnection && propBlock.nextConnection.targetBlock()
        }
        let oldPropSet = this.chosenProps
        this.chosenProps = _.uniq(newPropSet)
        this.updateShape_(oldPropSet)
      },
      updateShape_(oldSelection = []) {
        // Add new props
        for (const propName of this.chosenProps) {
          if (!this.getInput(propName)) {
            this.appendValueInput(propName)
              .setAlign(Blockly.ALIGN_RIGHT)
              .appendField((options[propName] && options[propName].label) || propName)
              .setCheck(options[propName] && options[propName].type)
          }
        }
        // Remove removed ones
        for (const propName of _.difference(oldSelection, this.chosenProps)) {
          this.removeInput(propName)
        }
        // Ensure correct order
        for (var i = this.chosenProps.length - 1; i > 0; i--) {
          this.moveInputBefore(this.chosenProps[i - 1], this.chosenProps[i])
        }
      }
    },
    function() {
      this.chosenProps = this.chosenProps || []
    },
    Object.keys(options).map(k => 'sirenia_options_mutator_' + k)
  )
}

defineBlock('javascript_named_thing', {
  message0: '%1',
  args0: [
    {
      type: 'field_input',
      name: 'thingName',
      text: ''
    }
  ],
  previousStatement: null,
  nextStatement: null
})

// This mutator can be used for things such as properties on an object or arguments for a function
// Dynamic in number and each with a name
function registerNamedThingsMutator(mutatorName, thingName, containerTip) {
  if (Blockly.Extensions.ALL_[mutatorName]) {
    return // Already registered
  }
  const containerBlockName = `javascript_${thingName}_mutator_container`
  defineBlock(containerBlockName, {
    message0: `${thingName} %1 %2`,
    args0: [
      {
        type: 'input_dummy'
      },
      {
        type: 'input_statement',
        name: 'CHOSEN_THINGS'
      }
    ],
    tooltip: containerTip
  })

  Blockly.Extensions.registerMutator(
    mutatorName,
    {
      mutationToDom() {
        // Serialize mutator info to xml
        if (!this.chosenThings || this.chosenThings.length === 0) {
          return null
        }
        let container = document.createElement('mutation')
        container.setAttribute('things', this.chosenThings.join(','))
        return container
      },
      domToMutation(container) {
        // Restore mutator info from xml
        let encodedThings = container.getAttribute('things')
        if (encodedThings) {
          this.chosenThings = encodedThings.split(',')
        }
        this.updateShape_()
      },
      decompose(workspace) {
        // Populate the mutator's dialog with the block's components
        let containerBlock = workspace.newBlock(containerBlockName)
        containerBlock.initSvg()
        let connection = containerBlock.getInput('CHOSEN_THINGS').connection
        for (const thing of this.chosenThings) {
          let thingBlock = workspace.newBlock('javascript_named_thing')
          thingBlock.initSvg()
          thingBlock.setFieldValue(thing, 'thingName')
          connection.connect(thingBlock.previousConnection)
          connection = thingBlock.nextConnection
        }
        return containerBlock
      },
      compose(containerBlock) {
        // Reconfigure this block based on the mutator dialog's components
        let thingBlock = containerBlock.getInputTargetBlock('CHOSEN_THINGS')
        let newThingSet = []
        // Get new thing selection
        while (thingBlock) {
          let thingName = thingBlock.getFieldValue('thingName')
          if (thingName) {
            newThingSet.push(thingName)
          }
          thingBlock = thingBlock.nextConnection && thingBlock.nextConnection.targetBlock()
        }
        let oldThingSet = this.chosenThings
        this.chosenThings = newThingSet
        this.updateShape_(oldThingSet)
      },
      updateShape_(oldSelection = []) {
        // Add new things
        for (const thingName of this.chosenThings) {
          if (!this.getInput(thingName)) {
            this.appendValueInput(thingName)
              .appendField(thingName)
              .setAlign(Blockly.ALIGN_RIGHT)
          }
        }
        // Remove removed ones
        for (const thingName of _.difference(oldSelection, this.chosenThings)) {
          this.removeInput(thingName)
        }
        // Ensure correct order
        for (var i = this.chosenThings.length - 1; i > 0; i--) {
          this.moveInputBefore(this.chosenThings[i - 1], this.chosenThings[i])
        }
      }
    },
    function() {
      this.chosenThings = this.chosenThings || []
    },
    ['javascript_named_thing']
  )
}

function registerCountMutator(mutatorName, itemText, containerText) {
  if (Blockly.Extensions.ALL_[mutatorName]) {
    return // Already registered
  }
  const mutatorContainerName = mutatorName + '_container'
  const mutatorItemName = mutatorName + '_item'
  defineBlock(mutatorItemName, {
    message0: `${itemText}`,
    args0: [],
    previousStatement: null,
    nextStatement: null
  })
  defineBlock(mutatorContainerName, {
    message0: `${containerText} %1 %2`,
    args0: [
      {
        type: 'input_dummy'
      },
      {
        type: 'input_statement',
        name: 'ITEMS'
      }
    ]
  })
  Blockly.Extensions.registerMutator(
    mutatorName,
    {
      mutationToDom() {
        // Serialize mutator info to xml
        if (this.count === null) {
          return null
        }
        let container = document.createElement('mutation')
        container.setAttribute('count', this.count)
        return container
      },
      domToMutation(container) {
        // Restore mutator info from xml
        let countAttr = container.getAttribute('count')
        if (countAttr !== null) {
          this.count = parseInt(countAttr, 10)
        }
        this.updateShape_()
      },
      decompose(workspace) {
        // Populate the mutator's dialog with the block's components
        let containerBlock = workspace.newBlock(mutatorContainerName)
        containerBlock.initSvg()
        let connection = containerBlock.getInput('ITEMS').connection
        for (let i = 0; i < this.count; i++) {
          let itemBlock = workspace.newBlock(mutatorItemName)
          itemBlock.initSvg()
          connection.connect(itemBlock.previousConnection)
          connection = itemBlock.nextConnection
        }
        return containerBlock
      },
      compose(containerBlock) {
        // Reconfigure this block based on the mutator dialog's components
        let itemBlock = containerBlock.getInputTargetBlock('ITEMS')
        // Get new thing selection
        let count = 0
        while (itemBlock) {
          count++
          itemBlock = itemBlock.nextConnection && itemBlock.nextConnection.targetBlock()
        }
        let oldCount = this.count
        this.count = count
        this.updateShape_(oldCount)
      },
      updateShape_(oldCount = 0) {
        let i = 0
        // Ensure the needed number of inputs are there
        for (; i < this.count; i++) {
          if (!this.getInput('ITEM' + i)) {
            this.appendValueInput('ITEM' + i)
          }
        }
        // Ensure there are not too many inputs
        for (; i < oldCount; i++) {
          this.removeInput('ITEM' + i)
        }
      }
    },
    null,
    [mutatorItemName]
  )
}

Blockly.Extensions.registerMutator('sirenia_output_type_mutator', {
  mutationToDom() {
    // Serialize mutator info to xml
    if (this.outputType == null) {
      return null
    }
    let container = document.createElement('mutation')
    container.setAttribute('outputtype', this.outputType)
    return container
  },
  domToMutation(container) {
    // Restore mutator info from xml
    this.outputType = container.getAttribute('outputtype')
    this.updateShape_()
  },
  updateShape_() {
    this.setOutput(true, this.outputType)
  }
})

Blockly.Extensions.registerMutator('sirenia_not_editable_mutator', {
  mutationToDom() {
    // Serialize mutator info to xml
    let container = document.createElement('mutation')
    container.setAttribute('not-editable', !!this.notEditable)
    return container
  },
  domToMutation(container) {
    // Restore mutator info from xml
    this.notEditable = container.getAttribute('not-editable') === 'true'
    this.updateShape_()
  },
  updateShape_() {
    this.setEditable(!this.notEditable)
  }
})

export default {
  registerOptionsMutator,
  registerNamedThingsMutator,
  registerCountMutator
}
